import React, { useState, useEffect, useRef } from 'react';
import {
  CurrencyEuroIcon,
  TagIcon,
  ArrowDownIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import ConfirmationModal from '../ConfirmationModal';
import AttributeLabel from '../AttributeLabel';
import { ModalShell, ModalActions } from './core';
import { documentDateToIso, localTodayIso, parseDocumentAmount } from '../../utils/documentFields';
import { useDocumentSession } from '../../utils/useDocumentSession';

interface QuickBookingDocument {
  id: number;
  name: string;
  invoice_amount?: string | number | null;
  invoice_date?: string | null;
  ai_comment?: string;
}

interface QuickBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  document: QuickBookingDocument | null;
}

interface Category {
  id: number;
  name: string;
}

interface Account {
  id: number;
  name: string;
  kontotyp: string;
}

const QuickBookingModal: React.FC<QuickBookingModalProps> = ({ isOpen, onClose, onSuccess, document: doc }) => {
  const isCurrentSession = useDocumentSession(`${isOpen}:${doc?.id ?? ''}`);
  const pendingBookings = useRef(new Map<number, number>());
  const submissionInFlight = useRef(false);
  const [createdBookingId, setCreatedBookingId] = useState<number | null>(null);
  const [completed, setCompleted] = useState(false);
  const [type, setType] = useState<'deposit' | 'withdrawal'>('withdrawal');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  useEffect(() => {
    if (isOpen && doc) {
      setLoading(false);
      submissionInFlight.current = false;
      setCompleted(false);
      setConfirmModal(previous => ({ ...previous, isOpen: false }));
      setCreatedBookingId(pendingBookings.current.get(doc.id) ?? null);
      fetchData();
      prefillFromDocument(doc);
    }
    // Reinitialize only on opening/document change, never while editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, doc]);

  const parseDateFromName = (name: string): string => {
    // Try patterns: 2026-01-15, 15.01.2026, 15-01-2026, 20260115
    const isoMatch = name.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

    const deMatch = name.match(/(\d{2})\.(\d{2})\.(\d{4})/);
    if (deMatch) return `${deMatch[3]}-${deMatch[2]}-${deMatch[1]}`;

    const deDashMatch = name.match(/(\d{2})-(\d{2})-(\d{4})/);
    if (deDashMatch) return `${deDashMatch[3]}-${deDashMatch[2]}-${deDashMatch[1]}`;

    const compactMatch = name.match(/(\d{4})(\d{2})(\d{2})/);
    if (compactMatch) {
      const m = parseInt(compactMatch[2]);
      const d = parseInt(compactMatch[3]);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        return `${compactMatch[1]}-${compactMatch[2]}-${compactMatch[3]}`;
      }
    }

    return localTodayIso();
  };

  const prefillFromDocument = (doc: QuickBookingDocument) => {
    const parsedAmount = parseDocumentAmount(doc.invoice_amount);
    setAmount(parsedAmount !== null ? Math.abs(parsedAmount).toFixed(2) : '');
    setType('withdrawal');
    setDate(documentDateToIso(doc.invoice_date) || documentDateToIso(parseDateFromName(doc.name)) || localTodayIso());

    // Description
    setDescription(doc.name.replace(/\.\w+$/, ''));
    setCategoryId('');
    setAccountId('');
  };

  const fetchData = async () => {
    try {
      const [categoriesRes, accountsRes] = await Promise.all([
        apiClient.get('/api/finance/categories'),
        apiClient.get('/api/finance/accounts'),
      ]);
      if (!isCurrentSession()) return;
      setCategories(categoriesRes.data);
      setAccounts(accountsRes.data);
    } catch {
      if (!isCurrentSession()) return;
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Daten konnten nicht geladen werden.',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!doc || submissionInFlight.current || completed) return;
    const parsedAmount = parseDocumentAmount(amount);
    const parsedDate = documentDateToIso(date);

    const pendingId = pendingBookings.current.get(doc.id);
    if (!pendingId && (parsedAmount === null || parsedAmount <= 0 || !parsedDate)) {
      setConfirmModal({ isOpen: true, type: 'error', title: 'Pflichtfelder', message: 'Bitte einen gültigen positiven Betrag und ein gültiges Datum eingeben.' });
      return;
    }
    if (!pendingId && !categoryId) {
      setConfirmModal({ isOpen: true, type: 'error', title: 'Kategorie fehlt', message: 'Bitte Kategorie auswählen.' });
      return;
    }
    if (!pendingId && !accountId) {
      setConfirmModal({ isOpen: true, type: 'error', title: 'Konto fehlt', message: 'Bitte Konto auswählen.' });
      return;
    }

    submissionInFlight.current = true;
    setLoading(true);
    try {
      // Keep the created booking across link retries and reopening this document.
      let buchungId = pendingBookings.current.get(doc.id);
      if (!buchungId) {
        const formData = new FormData();
        formData.append('type', type);
        formData.append('amount', Math.abs(parsedAmount!).toString());
        formData.append('date', parsedDate!);
        formData.append('category_id', categoryId);
        formData.append('account_id', accountId);
        formData.append('description', description || '');

        const bookingRes = await apiClient.post('/api/finance/bookings', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        buchungId = Number(bookingRes.data?.buchung_id);
        if (!Number.isInteger(buchungId) || buchungId <= 0) throw new Error('Die Buchung wurde ohne gültige ID beantwortet. Bitte die Buchungsliste prüfen.');
        pendingBookings.current.set(doc.id, buchungId);
        if (isCurrentSession()) setCreatedBookingId(buchungId);
      }

      // 2. Link document to booking
      if (buchungId) {
        await apiClient.post(`/api/finance/statements/${buchungId}/documents`, {
          document_ids: [doc.id],
        });
      }

      if (!isCurrentSession()) return;
      setCompleted(true);
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Buchung erstellt',
        message: 'Buchung wurde erstellt und mit dem Dokument verknüpft.',
      });

    } catch (err: any) {
      if (!isCurrentSession()) return;
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: pendingBookings.current.has(doc.id)
          ? `Buchung ${pendingBookings.current.get(doc.id)} wurde erstellt. Die Verknüpfung ist fehlgeschlagen. Erneut versuchen verknüpft dieselbe Buchung.`
          : err.response?.data?.error || err.message || 'Buchung konnte nicht erstellt werden.',
      });
    } finally {
      if (isCurrentSession()) { submissionInFlight.current = false; setLoading(false); }
    }
  };

  const handleClose = () => {
    if (submissionInFlight.current) return;
    if (completed) {
      if (doc) pendingBookings.current.delete(doc.id);
      onSuccess?.();
    }
    onClose();
  };

  if (!doc) return null;

  return (
    <>
      <ModalShell
        open={isOpen}
        onClose={handleClose}
        variant="form"
        size="md"
        title="Buchung erstellen"
        subtitle={doc.name}
        icon={CurrencyEuroIcon}
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{ label: 'Abbrechen', onClick: handleClose, disabled: loading }}
            primary={{
              label: loading ? 'Wird gespeichert…' : completed ? 'Fertig' : createdBookingId ? 'Verknüpfung erneut versuchen' : 'Buchung erstellen',
              onClick: () => completed ? handleClose() : formRef.current?.requestSubmit(),
              loading,
              disabled: loading || (!completed && !createdBookingId && (!categoryId || !accountId)),
            }}
          />
        }
      >
        {createdBookingId && !completed && (
          <p role="status" className="mb-4 rounded-xl border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-200">
            Buchung {createdBookingId} ist bereits erstellt. Beim Wiederholen wird nur das Dokument verknüpft.
          </p>
        )}
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          <fieldset disabled={loading || !!createdBookingId || completed} className="space-y-4">
          {/* Typ */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Typ
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setType('withdrawal')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  type === 'withdrawal'
                    ? 'bg-error-50 dark:bg-error-900/20 border-error-500 text-error-700 dark:text-error-300'
                    : 'bg-gray-50 dark:bg-[#0f1419] border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                <ArrowUpIcon className="h-5 w-5 mx-auto mb-1 text-error-600" />
                <span className="text-xs font-medium">Auszahlung</span>
              </button>
              <button
                type="button"
                onClick={() => setType('deposit')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  type === 'deposit'
                    ? 'bg-success-50 dark:bg-success-900/20 border-success-500 text-success-700 dark:text-success-300'
                    : 'bg-gray-50 dark:bg-[#0f1419] border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                <ArrowDownIcon className="h-5 w-5 mx-auto mb-1 text-success-600" />
                <span className="text-xs font-medium">Einzahlung</span>
              </button>
            </div>
          </div>

          {/* Betrag und Datum */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <AttributeLabel iconKey="amount" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                Betrag <span className="text-error-500">*</span>
              </AttributeLabel>
              <div className="relative">
                <CurrencyEuroIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Betrag in Euro"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  placeholder="0,00"
                  className="w-full pl-10 pr-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white placeholder-gray-400"
                  disabled={loading}
                />
              </div>
            </div>
            <div>
              <AttributeLabel iconKey="date" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                Datum <span className="text-error-500">*</span>
              </AttributeLabel>
              <input
                type="date"
                aria-label="Buchungsdatum"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-full px-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                disabled={loading}
              />
            </div>
          </div>

          {/* Kategorie */}
          <div>
            <AttributeLabel iconKey="category" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
              Kategorie <span className="text-error-500">*</span>
            </AttributeLabel>
            <div className="relative">
              <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
              <select
                aria-label="Kategorie"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                required
                className="w-full pl-10 pr-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white appearance-none"
                disabled={loading}
              >
                <option value="">Kategorie wählen</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Konto */}
          <div>
            <AttributeLabel iconKey="bankAccount" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
              Konto <span className="text-error-500">*</span>
            </AttributeLabel>
            <select
              aria-label="Konto"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              required
              className="w-full px-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white appearance-none"
              disabled={loading}
            >
              <option value="">Konto wählen</option>
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>{acc.name}</option>
              ))}
            </select>
          </div>

          {/* Beschreibung */}
          <div>
            <AttributeLabel iconKey="description" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
              Beschreibung
            </AttributeLabel>
            <textarea
              aria-label="Beschreibung"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Verwendungszweck"
              className="w-full px-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
              rows={2}
              disabled={loading}
            />
          </div>
          </fieldset>
        </form>
      </ModalShell>

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        onClose={() => { setConfirmModal({ ...confirmModal, isOpen: false }); if (completed) handleClose(); }}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant="single"
        showCloseButton={true}
        button={{
          text: 'OK',
          onPress: () => { setConfirmModal({ ...confirmModal, isOpen: false }); if (completed) handleClose(); },
        }}
      />
    </>
  );
};

export default QuickBookingModal;
